import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PrimaryButton } from "@/components/brand/PrimaryButton";

interface Pol {
  id: string;
  client_id: string;
  agent_id: string | null;
  carrier: string | null;
  product_type: string | null;
  policy_number: string | null;
  face_amount: number | null;
  premium: number | null;
  status: string;
  effective_date: string | null;
}

const fmt = (n: number | null) => n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export const Route = createFileRoute("/admin/policies")({
  head: () => ({ meta: [{ title: "Policies — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Command Center" nav={ADMIN_NAV} tourKey="admin_tour">
        <AdminPolicies />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function AdminPolicies() {
  const [rows, setRows] = useState<Pol[]>([]);
  const [profiles, setProfiles] = useState<Map<string, string>>(new Map());
  const [clients, setClients] = useState<Map<string, string>>(new Map());
  const [status, setStatus] = useState("all");
  const [carrier, setCarrier] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => { void load(); }, []);

  async function load() {
    const [pol, prof, cli] = await Promise.all([
      supabase.from("policies").select("id, client_id, agent_id, carrier, product_type, policy_number, face_amount, premium, status, effective_date").order("created_at", { ascending: false }),
      supabase.from("profiles").select("id, full_name"),
      supabase.from("clients").select("id, user_id"),
    ]);
    setRows((pol.data ?? []) as Pol[]);
    const pm = new Map<string, string>();
    for (const p of (prof.data ?? []) as Array<{ id: string; full_name: string | null }>) pm.set(p.id, p.full_name || "Unnamed");
    setProfiles(pm);
    const cm = new Map<string, string>();
    for (const c of (cli.data ?? []) as Array<{ id: string; user_id: string }>) cm.set(c.id, pm.get(c.user_id) || "Client");
    setClients(cm);
  }

  const carriers = useMemo(() => Array.from(new Set(rows.map(r => r.carrier).filter(Boolean) as string[])), [rows]);

  const filtered = useMemo(() => rows.filter(r =>
    (status === "all" || r.status === status) &&
    (carrier === "all" || r.carrier === carrier) &&
    (!q || (r.policy_number ?? "").toLowerCase().includes(q.toLowerCase()) || (clients.get(r.client_id) ?? "").toLowerCase().includes(q.toLowerCase()))
  ), [rows, status, carrier, q, clients]);

  function exportCsv() {
    const header = ["Policy #", "Client", "Carrier", "Product", "Face Amount", "Premium", "Status", "Strategist", "Effective"];
    const lines = filtered.map(r => [r.policy_number ?? "", clients.get(r.client_id) ?? "", r.carrier ?? "", r.product_type ?? "", r.face_amount ?? "", r.premium ?? "", r.status, profiles.get(r.agent_id ?? "") ?? "—", r.effective_date ?? ""].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `policies-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  const totalAUM = filtered.reduce((s, r) => s + (Number(r.face_amount) || 0), 0);

  return (
    <PortalPage eyebrow="Business" title="Policies" intro={`${filtered.length} policies · ${fmt(totalAUM)} in protection`} actions={<PrimaryButton onClick={exportCsv}>Export CSV</PrimaryButton>}>
      <div className="flex flex-wrap gap-3 mb-6">
        <Input placeholder="Search policy # or client" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="lapsed">Lapsed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={carrier} onValueChange={setCarrier}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Carrier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All carriers</SelectItem>
            {carriers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-bronze-600/20 rounded-[4px] overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-left text-charcoal-500 font-sans uppercase tracking-[0.12em] text-[10px]">
            <tr className="border-b border-bronze-600/20">
              <th className="px-4 py-3">Policy #</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Carrier</th><th className="px-4 py-3">Face</th><th className="px-4 py-3">Premium</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Strategist</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-charcoal-500">No policies match these filters.</td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className="border-b border-bronze-600/10 hover:bg-cream-50">
                <td className="px-4 py-3 font-medium text-navy-800">{r.policy_number ?? "—"}</td>
                <td className="px-4 py-3">{clients.get(r.client_id) ?? "—"}</td>
                <td className="px-4 py-3 text-charcoal-500">{r.carrier ?? "—"}</td>
                <td className="px-4 py-3">{fmt(r.face_amount as any)}</td>
                <td className="px-4 py-3">{fmt(r.premium as any)}</td>
                <td className="px-4 py-3 capitalize">{r.status}</td>
                <td className="px-4 py-3 text-charcoal-500">{profiles.get(r.agent_id ?? "") ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PortalPage>
  );
}
